import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import useApi from "../../hook/useAPI";
import { SUBSCRIPTION } from "../../utils/const";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useContextValue } from "../../context/LoaderContext";
import {
  descriptionToFormValue,
  formatCurrencyAmount,
  BILLING_CURRENCY,
  getPlanCatalogType,
  getPlanCatalogTypeLabel,
} from "../../utils/planCatalogDisplay";

const EditSubscription = ({ show, handleClose, subscriptionId, getPlans }) => {
  const { getData, putData } = useApi();
  const initialState = {
    planName: "",
    description: "",
    monthlyAmount: "",
    yearlyAmount: "",
    stripeProductId: "",
    stripePriceId: "",
    stripeMonthlyPriceId: "",
    stripeYearlyPriceId: "",
    note: "",
  };
  const [priceData, setPriceData] = useState(initialState);
  const planNamePattern = /^[a-zA-Z0-9\s\-_',.()]+$/;
  const formRef = useRef();
  const {setLoading} = useContextValue()

  const normalizeDescription = (value) => descriptionToFormValue(value);

  const callAPI = useCallback(async () => {
    if (subscriptionId) {
      try {
        setLoading(true)
        const data = await getData(
          `${SUBSCRIPTION.GET_PRICE}?id=${subscriptionId}`
        );
        setPriceData(data?.data[0]);
        setLoading(false)
      } catch (e) {
        toast.error(e?.message, {
          hideProgressBar: true,
          theme: "colored",
        });
      } 
    }
  }, [getData, setLoading, subscriptionId]);

  useEffect(() => {
    if (show) {
      callAPI();
    }
  }, [callAPI, show]);

  const handleSubmit = async (formValues) => {
    const editableFields = [
      "planName",
      "description",
      "monthlyAmount",
      "yearlyAmount",
    ];
    const data = editableFields.reduce((payload, field) => {
      let value;
      let previousValue;

      if (field === "monthlyAmount") {
        value = Number(formValues[field]);
        previousValue = Number(priceData.monthlyPrice ?? priceData.price);
      } else if (field === "yearlyAmount") {
        value = formValues[field] === "" ? undefined : Number(formValues[field]);
        previousValue = priceData.yearlyPrice === undefined || priceData.yearlyPrice === null
          ? undefined
          : Number(priceData.yearlyPrice);
      } else if (field === "description") {
        value = formValues.description?.trim() || "";
        previousValue = normalizeDescription(priceData.description).trim();
      } else {
        value = formValues[field]?.trim() || "";
        previousValue = (priceData[field] || "").trim();
      }

      if (value !== previousValue) {
        payload[field] = value;
      }
      return payload;
    }, {});
    if (formValues.note?.trim()) {
      data.note = formValues.note.trim();
    }
    if (subscriptionId && Object.keys(data).some((field) => field !== "note")) {
      putData(`${SUBSCRIPTION.PRICE_EDIT}/${subscriptionId}`, data)
        .then((response) => {
          if (response.status === 200) {
            setPriceData(response?.data || formValues);
            const pricingAuditNote = response?.changeRecordId
              ? ` ${response?.affectedUserCount || 0} enrolled user(s) recorded for pricing.`
              : "";
            toast.success(
              `${response?.message || "Plan updated successfully."}${pricingAuditNote}`,
              {
              hideProgressBar: true,
              theme: "colored",
              }
            );
            handleClose();
            getPlans(); 
          } else {
            toast.error(response.message, {
              hideProgressBar: true,
              theme: "colored",
            });
          }
        })
        .catch((e) => {
          toast.error(e?.response?.data?.message || e?.response?.data?.error || e?.message || "Unable to update Stripe pricing.", {
            hideProgressBar: true,
            theme: "colored",
          });
        });
    } else {
      handleClose();
    }
  };

  const handleCloseModal = () => {
    handleClose();
    setPriceData(initialState); 
  };
  

  const handleChange = async (e, fieldName) => {
    formRef?.current?.setFieldValue(fieldName, e.target.value);
    await formRef?.current?.validateField(fieldName);
    if (formRef?.current?.errors?.[fieldName] !== undefined) {
      formRef?.current?.setFieldError(
        fieldName,
        formRef?.current?.errors?.[fieldName]
      );
      formRef?.current?.setFieldTouched(fieldName, true);
    }
  };

  const priceSchema = Yup.object().shape({
    planName: Yup.string()
      .required("Please enter plan name")
      .matches(
        planNamePattern,
        "Please enter a valid plan name only characters allowed."
      ),
    monthlyAmount: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .typeError("Monthly amount must be a number")
      .positive("Monthly amount must be positive")
      .required("Please enter monthly amount"),
    yearlyAmount: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .nullable()
      .typeError("Yearly amount must be a number")
      .positive("Yearly amount must be positive")
      .notRequired(),
    note: Yup.string().trim().max(500, "Note cannot exceed 500 characters"),
    description: Yup.string().trim(),
  });

  const getChangedFields = (values) => [
    ["planName", "Plan name"],
    ["description", "Description"],
    ["monthlyAmount", "Monthly amount"],
    ["yearlyAmount", "Yearly amount"],
  ].filter(([field]) => {
    let currentValue;
    let previousValue;

    if (field === "monthlyAmount") {
      currentValue = Number(values[field]);
      previousValue = Number(priceData.monthlyPrice ?? priceData.price);
    } else if (field === "yearlyAmount") {
      currentValue = values[field] === "" ? undefined : Number(values[field]);
      previousValue = priceData.yearlyPrice === undefined || priceData.yearlyPrice === null
        ? undefined
        : Number(priceData.yearlyPrice);
    } else if (field === "description") {
      currentValue = values.description?.trim() || "";
      previousValue = normalizeDescription(priceData.description).trim();
    } else {
      currentValue = values[field]?.trim() || "";
      previousValue = (priceData[field] || "").trim();
    }

    return currentValue !== previousValue;
  });

  return (
    <>
      <Modal
        show={show}
        onHide={handleCloseModal}
        centered
        size="xl"
        className="conversion_modal stripe-pricing-modal"
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title>Edit plan catalog entry</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="modal-body">
              <div className="dialog-content-body modalbody-form-filed">
                <Formik
                  onSubmit={(formData) => handleSubmit(formData)}
                  initialValues={{
                    planName: priceData.planName,
                    description: descriptionToFormValue(priceData.description),
                    monthlyAmount: priceData.monthlyPrice ?? priceData.price ?? "",
                    yearlyAmount: priceData.yearlyPrice ?? "",
                    stripeProductId: priceData.stripeProductId || "",
                    stripePriceId: priceData.stripePriceId || "",
                    stripeMonthlyPriceId: priceData.stripeMonthlyPriceId || "",
                    stripeYearlyPriceId: priceData.stripeYearlyPriceId || "",
                    note: "",
                  }}
                  enableReinitialize
                  validationSchema={priceSchema}
                  innerRef={formRef}
                >
                  {({ errors, values, touched, isValid, dirty }) => {
                    const changedFields = getChangedFields(values);
                    const catalogType = getPlanCatalogType(priceData);
                    const catalogTypeLabel = getPlanCatalogTypeLabel(priceData);
                    const currency = BILLING_CURRENCY.toUpperCase();
                    return (
                    <Form>
                      <div className="add-device-form stripe-pricing-form">
                        <div className="stripe-form-hero">
                          <div>
                            <span className="stripe-eyebrow">Stripe catalog</span>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <h4 className="mb-0">{values.planName || "Plan"}</h4>
                              <span className={`plan-catalog-type-badge ${catalogType}`}>{catalogTypeLabel}</span>
                            </div>
                            <p>
                              {catalogType === "addon"
                                ? "Add-on plans attach to Basic subscriptions only. Edit display copy and Stripe prices here."
                                : "Subscription plans are the base tier (Basic or All-in-One). Edit display copy and Stripe prices here."}
                            </p>
                          </div>
                          <div className="stripe-plan-price">
                            <span>{formatCurrencyAmount(values.monthlyAmount, currency)} / month</span>
                            <small>
                              {values.yearlyAmount
                                ? `${formatCurrencyAmount(values.yearlyAmount, currency)} / year`
                                : "Yearly price not set"}
                            </small>
                          </div>
                        </div>

                        <section className="stripe-form-section">
                          <div className="stripe-section-heading">
                            <div>
                              <span>Plan display</span>
                              <small>Shown to admins and customers in LocShark.</small>
                            </div>
                          </div>
                          <div className="row gx-md-4">
                          <div className="col-md-6">
                            <div className="form_field_wrapper">
                              <label>Plan name</label>
                              <Field
                                className="form-control"
                                placeholder="Enter your Title"
                                type="text"
                                label="Title"
                                name="planName"
                                value={values.planName}
                                onChange={(e) => handleChange(e, "planName")}
                                
                              />
                              {touched?.planName && errors?.planName && (
                                <p style={{ color: "red" }}>
                                  {errors?.planName}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form_field_wrapper">
                              <label>Currency</label>
                              <input className="form-control" value={currency} readOnly disabled />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form_field_wrapper">
                              <label>Monthly amount ({currency})</label>
                              <Field
                                className="form-control"
                                placeholder="Enter monthly amount"
                                type="number"
                                min="0.01"
                                step="0.01"
                                label="Enter Price"
                                name="monthlyAmount"
                                value={values.monthlyAmount}
                                onChange={(e) => handleChange(e, "monthlyAmount")}
                              />
                              {touched?.monthlyAmount && errors?.monthlyAmount && (
                                <p style={{ color: "red" }}>{errors?.monthlyAmount}</p>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form_field_wrapper">
                              <label>Yearly amount ({currency})</label>
                              <Field
                                className="form-control"
                                placeholder="Enter yearly amount"
                                type="number"
                                min="0.01"
                                step="0.01"
                                name="yearlyAmount"
                                value={values.yearlyAmount}
                                onChange={(e) => handleChange(e, "yearlyAmount")}
                              />
                              {touched?.yearlyAmount && errors?.yearlyAmount && (
                                <p style={{ color: "red" }}>{errors?.yearlyAmount}</p>
                              )}
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div className="form_field_wrapper">
                              <label>Feature list (one line per bullet)</label>
                              <div className="form-floating">
                                <Field
                                  as="textarea"
                                  name="description"
                                  className="form-control"
                                  placeholder={"Track real-time location\nRemote lock/unlock"}
                                  id="floatingTextarea"
                                  rows={5}
                                  style={{ height: "140px", maxHeight: "100%" }}
                                  value={values.description}
                                  onChange={(e) =>
                                    handleChange(e, "description")
                                  }
                                />
                                {touched?.description &&
                                  errors?.description && (
                                    <p style={{ color: "red" }}>
                                      {errors?.description}
                                    </p>
                                  )}
                              </div>
                            </div>
                          </div>
                          </div>
                        </section>

                        <section className="stripe-form-section">
                          <div className="stripe-section-heading">
                            <div>
                              <span>Current Stripe identifiers</span>
                              <small>New Price IDs are generated automatically when amounts change.</small>
                            </div>
                            <span className="stripe-status-badge">Existing subscribers protected</span>
                          </div>
                          <div className="stripe-safety-note">
                            <strong>Automatic Stripe Price creation.</strong>
                            <span>Saving a changed amount creates a new Stripe Price ID. Existing subscribers remain linked to their previous Price IDs.</span>
                          </div>
                          <div className="row gx-md-4">
                          {[
                            ["Stripe Product ID", values.stripeProductId],
                            ["Monthly Stripe Price ID", values.stripeMonthlyPriceId],
                            ["Yearly Stripe Price ID", values.stripeYearlyPriceId],
                          ].map(([label, value]) => (
                            <div className="col-md-6" key={label}>
                              <div className="stripe-readonly-id">
                                <span>{label}</span>
                                <code>{value || "-"}</code>
                              </div>
                            </div>
                          ))}
                          </div>
                        </section>

                        <section className="stripe-form-section">
                          <div className="stripe-section-heading">
                            <div>
                              <span>Audit note</span>
                              <small>Explain why this catalog change is being made.</small>
                            </div>
                          </div>
                          <div className="row">
                          <div className="col-md-12">
                            <div className="form_field_wrapper">
                              <label>Change Note</label>
                              <Field
                                as="textarea"
                                className="form-control"
                                name="note"
                                rows={3}
                                value={values.note}
                                onChange={(e) => handleChange(e, "note")}
                                placeholder="Reason for this pricing or Stripe mapping change"
                              />
                              {touched?.note && errors?.note && (
                                <p style={{ color: "red" }}>{errors.note}</p>
                              )}
                            </div>
                          </div>
                          </div>
                        </section>

                        <div className={`stripe-change-summary ${changedFields.length > 0 ? "has-changes" : ""}`}>
                          <div>
                            <strong>{changedFields.length > 0 ? `${changedFields.length} change(s) ready` : "No changes yet"}</strong>
                            <span>
                              {changedFields.length > 0
                                ? changedFields.map(([, label]) => label).join(", ")
                                : "Edit a value above to enable the update."}
                            </span>
                          </div>
                          <div className="stripe-form-actions">
                            <button type="button" className="btn btn-light" onClick={handleCloseModal}>
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={!isValid || !dirty || changedFields.length === 0}
                            >
                              Save plan changes
                            </button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  )}}
                </Formik>
              </div>
            </div>
          </Modal.Body>
        </>
      </Modal>
    </>
  );
};

export default EditSubscription;
